import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Row,
  Col,
  Spin,
  message,
  Button,
  Typography,
  Modal,
  Tooltip,
} from "antd";
import { RefreshCw, PlusCircle } from "lucide-react";
import CrawlConfig from "./CrawlConfig";
import CrawlCard from "./CrawlCard";
import "./index.css";

const { Title } = Typography;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SuperAdminContentIngestion = () => {
  const { t } = useTranslation();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    loading: false,
    success: false,
    config: null,
    error: null,
  });
  const [successModalType, setSuccessModalType] = useState("create"); // "create" or "edit"
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [editConfig, setEditConfig] = useState(null);

  const fetchConfigs = () => {
    setLoading(true);
    fetch(`${BACKEND_URL}/api/v1/content-ingestion/crawl/config/all`, {
      method: "GET",
      credentials: "include",
    })
      .then((res) => res.json())
      .then((resp) => setConfigs(resp.data))
      .catch((err) => {
        message.error(`Failed to fetch crawl configs: ${err.message}`);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchConfigs();
  }, []);

  const handleEdit = (config) => {
    setEditConfig(config);
    setConfigModalOpen(true);
  };

  const handleDelete = (config) => {
    setDeleteModal({ open: true, loading: false, success: false, config });
  };

  const confirmDelete = () => {
    setDeleteModal((prev) => ({ ...prev, loading: true, error: null }));
    fetch(
      `${BACKEND_URL}/api/v1/content-ingestion/crawl/config/${encodeURIComponent(deleteModal.config.client_id)}`,
      {
        method: "DELETE",
        credentials: "include",
      },
    )
      .then((res) => {
        if (res.status !== 200) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        setConfigs((prev) =>
          prev.filter((cfg) => cfg.client_id !== deleteModal.config.client_id),
        );
        setDeleteModal((prev) => ({
          ...prev,
          loading: false,
          success: true,
          error: null,
        }));
      })
      .catch((err) => {
        setDeleteModal((prev) => ({
          ...prev,
          loading: false,
          error: `Failed to delete crawl config: ${err.message}`,
        }));
      })
      .finally(() => {
        fetchConfigs();
      });
  };

  return (
    <div className="crawl-manager-container">
      <div style={{ marginBottom: 8 }}>
        <Title level={2} style={{ margin: 0 }}>
          {t("superAdmin.contentIngestion.title", "Crawl Manager")}
        </Title>
      </div>
      <div style={{ marginBottom: 16 }}>
        <Tooltip title={t("superAdmin.contentIngestion.refresh", "Refresh")}>
          <Button
            className="crawl-refresh-btn"
            onClick={fetchConfigs}
            loading={loading}
            disabled={loading}
            icon={<RefreshCw size={20} />}
            type="default"
          >
            <span className="crawl-refresh-btn-text">
              {t("superAdmin.contentIngestion.refresh", "Refresh")}
            </span>
          </Button>
        </Tooltip>
      </div>
      {loading ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginTop: 40,
          }}
        >
          <Spin size="large" />
          <div style={{ marginTop: 16, fontSize: 18, color: "#555" }}>
            {t(
              "superAdmin.contentIngestion.loadingConfigs",
              "Loading crawl configurations...",
            )}
          </div>
        </div>
      ) : (
        <>
          <Row gutter={[24, 24]}>
            {configs.map((cfg) => (
              <Col xs={24} sm={12} md={8} lg={6} key={cfg.client_id}>
                <CrawlCard
                  cfg={cfg}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              </Col>
            ))}
          </Row>
          <div className="crawl-manager-add-btn-container">
            <Button
              type="primary"
              size="large"
              onClick={() => {
                setEditConfig(null);
                setConfigModalOpen(true);
              }}
            >
              <PlusCircle />
              <span>
                {t(
                  "superAdmin.contentIngestion.addNewCrawl",
                  "Add New Crawl",
                )}
              </span>
            </Button>
          </div>
          <Modal
            open={configModalOpen}
            title={
              <Title level={3} className="crawl-manager-modal-title">
                {editConfig
                  ? t(
                      "superAdmin.contentIngestion.editCrawlConfig",
                      "Edit Crawl Config",
                    )
                  : t(
                      "superAdmin.contentIngestion.addNewCrawlConfig",
                      "Add New Crawl Config",
                    )}
              </Title>
            }
            onCancel={() => {
              setConfigModalOpen(false);
            }}
            footer={null}
            destroyOnHidden
          >
            <CrawlConfig
              clientIdsWithConfig={configs.map((cfg) => cfg.client_id)}
              closeModal={() => {
                setConfigModalOpen(false);
                setEditConfig(null);
              }}
              editConfig={editConfig}
              onSuccess={() => {
                setConfigModalOpen(false);
                setSuccessModalOpen(true);
                setSuccessModalType(!!editConfig ? "edit" : "create");
                fetchConfigs();
              }}
            />
          </Modal>
          <Modal
            open={deleteModal.open}
            onCancel={() =>
              setDeleteModal({
                open: false,
                loading: false,
                success: false,
                config: null,
                error: null,
              })
            }
            footer={null}
            destroyOnHidden
            styles={{ body: { padding: 12, minHeight: "unset" } }}
            className="crawl-manager-delete-modal"
          >
            {deleteModal.success ? (
              <div className="crawl-manager-delete-success">
                <Title level={4} className="crawl-manager-delete-success-title">
                  {t(
                    "superAdmin.contentIngestion.deletedSuccessfully",
                    "Deleted successfully!",
                  )}
                </Title>
                <p className="crawl-manager-delete-success-desc">
                  {t(
                    "superAdmin.contentIngestion.closeDialogNotice",
                    "You may close this dialog.",
                  )}
                </p>
              </div>
            ) : deleteModal.error ? (
              <div className="crawl-manager-delete-error">
                <Title level={5} className="crawl-manager-delete-error-title">
                  {t("superAdmin.contentIngestion.error", "Error")}
                </Title>
                <div className="crawl-manager-delete-error-desc">
                  {deleteModal.error}
                </div>
              </div>
            ) : (
              <>
                <Title level={4} className="crawl-manager-delete-confirm-title">
                  {t(
                    "superAdmin.contentIngestion.confirmDeletion",
                    "Confirm Deletion",
                  )}
                </Title>
                <p className="crawl-manager-delete-confirm-desc">
                  {t(
                    "superAdmin.contentIngestion.confirmDeleteCrawlConfigPrefix",
                    "Are you sure you want to delete crawl config for client ",
                  )}
                  <strong>{deleteModal.config?.client_id}</strong>
                  {t(
                    "superAdmin.contentIngestion.confirmDeleteCrawlConfigSuffix",
                    "?",
                  )}
                </p>
                <div className="crawl-manager-delete-actions">
                  <Button
                    onClick={() =>
                      setDeleteModal({
                        open: false,
                        loading: false,
                        success: false,
                        config: null,
                        error: null,
                      })
                    }
                    style={{ marginRight: 0 }}
                    disabled={deleteModal.loading}
                  >
                    {t("common.cancel", "Cancel")}
                  </Button>
                  <Button
                    danger
                    type="primary"
                    onClick={confirmDelete}
                    loading={deleteModal.loading}
                  >
                    {t("common.delete", "Delete")}
                  </Button>
                </div>
              </>
            )}
          </Modal>
          <Modal
            open={successModalOpen}
            onCancel={() => setSuccessModalOpen(false)}
            footer={null}
            destroyOnHidden
          >
            <div style={{ textAlign: "center", padding: 24 }}>
              {successModalType === "edit" ? (
                <>
                  <Title level={4} style={{ color: "#52c41a" }}>
                    {t(
                      "superAdmin.contentIngestion.crawlConfigEdited",
                      "Crawl Config Edited!",
                    )}
                  </Title>
                  <p>
                    {t(
                      "superAdmin.contentIngestion.crawlConfigEditedDesc",
                      "Your crawl configuration was edited successfully.",
                    )}
                  </p>
                </>
              ) : (
                <>
                  <Title level={4} style={{ color: "#52c41a" }}>
                    {t(
                      "superAdmin.contentIngestion.crawlConfigCreated",
                      "Crawl Config Created!",
                    )}
                  </Title>
                  <p>
                    {t(
                      "superAdmin.contentIngestion.crawlConfigCreatedDesc",
                      "Your crawl configuration was created successfully.",
                    )}
                  </p>
                </>
              )}
            </div>
          </Modal>
        </>
      )}
    </div>
  );
};

export default SuperAdminContentIngestion;
